/**
 * LinkedIn Integration API Service (PROJECT-085)
 * Per-entity LinkedIn OAuth connect/status/disconnect — lets a bot publish to LinkedIn
 * as the connected user/org instead of a fixed account. Mirrors googleIntegrationApi.ts.
 */

import AuthUtils from "@/providers/firebase/authUtils";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

export interface ApiResponse<T = Record<string, unknown>> {
  status: boolean;
  statusCode: number;
  message: string;
  data: T | null;
}

async function apiCall<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const headers = await AuthUtils.getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...headers, ...options.headers },
  });
  return res.json();
}

export interface LinkedInConnectUrlResponse {
  authorization_url: string;
}

export interface LinkedInIntegrationStatus {
  connected: boolean;
  name: string | null;
  author_urn: string | null;
  /** T35 — the vault's status vocabulary; existence alone is not health. */
  status: string | null;
  /** The ACCESS token's expiry, the only expiry LinkedIn reports (same meaning as Google's). */
  expiresAt: string | null;
  /** Which author identity the GRANT permits: `member` | `organization`. */
  target: string | null;
  connectedAt: string | null;
}

export interface LinkedInCredentialStatus {
  status: string;
  expiresAt: string | null;
}

export async function getLinkedInConnectUrl(
  entityId: string,
  target: string = "member",
): Promise<ApiResponse<LinkedInConnectUrlResponse>> {
  return apiCall("/api/v1/linkedin/connect", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId, target }),
  });
}

export async function getLinkedInIntegrationStatus(
  entityId: string,
): Promise<ApiResponse<LinkedInIntegrationStatus>> {
  return apiCall(`/api/v1/linkedin/status?entity_id=${encodeURIComponent(entityId)}`);
}

/**
 * T35 — check the stored credential now, instead of inferring health from the row's existence.
 * The BE answers a STATUS (never a 500), and an unreachable LinkedIn leaves a working credential
 * marked as it was, so this can be offered as a plain `Check` button.
 */
export async function verifyLinkedInCredential(
  entityId: string,
): Promise<ApiResponse<LinkedInCredentialStatus>> {
  return apiCall("/api/v1/linkedin/verify_credential", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId }),
  });
}

export async function disconnectLinkedInIntegration(
  entityId: string,
): Promise<ApiResponse<{ disconnected: boolean }>> {
  return apiCall("/api/v1/linkedin/disconnect", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId }),
  });
}
